import axios from 'axios';

// Environment configuration
const ENV = process.env.REACT_APP_ENV || 'staging';

const API_ROOTS = {
  development: {
    auth: 'http://localhost:5091/api',
    // auth: 'https://amalgamate-user-module-api-stagging.up.railway.app/api',
    vote: 'http://localhost:5119/api',
    // vote: 'https://amalgamate-voting-api-staging.up.railway.app/api',
    shortsVideo: 'http://localhost:5176/api',
    // shortsVideo: 'https://amalgamate-video-api-staging.up.railway.app/api',
    questionnaire: 'https://amalgamatequestionariesapi-qa.azurewebsites.net/api'
  },
  staging: {
    auth: 'https://amalgamate-user-module-api-production.up.railway.app/api',
    vote: 'https://amalgamate-voting-api-production.up.railway.app/api',
    shortsVideo: 'https://amalgamate-video-api-production.up.railway.app/api',
    questionnaire: 'https://amalgamatequestionariesapistaging.azurewebsites.net/api'
  },
  
  qa: {
    auth: 'https://amalgamate-user-module-api-stagging.up.railway.app/api',
    vote: 'https://amalgamate-voting-api-staging.up.railway.app/api',
    shortsVideo: 'https://amalgamate-video-api-staging.up.railway.app/api',
    questionnaire: 'https://amalgamatequestionariesapi-qa.azurewebsites.net/api'
  },

};

// Select URLs based on current environment
const selectedEnv = API_ROOTS[ENV] || API_ROOTS.staging;
const AUTH_BASE_URL = selectedEnv.auth;
const VOTE_BASE_URL = selectedEnv.vote;
const SHORTS_VIDEO_URL = selectedEnv.shortsVideo;
const QUESTIONNAIRE_URL = selectedEnv.questionnaire;



const cookieUtils = {
  getCookie: (name) => {
    try {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) {
        const cookieValue = parts.pop().split(';').shift();
        return cookieValue ? decodeURIComponent(cookieValue) : null;
      }
    } catch (error) {
      console.error('Error reading cookie:', error);
    }
    return null;
  },

  setCookie: (name, value, days = 7) => {
    try {
      const expires = new Date();
      expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
      document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
    } catch (error) {
      console.error('Error setting cookie:', error);
    }
  },

  removeCookie: (name) => {
    try {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict`;
    } catch (error) {
      console.error('Error removing cookie:', error);
    }
  }
};

export const tokenStorage = {
  getToken: () => {
    // Try localStorage first, then cookies
    return localStorage.getItem('accessToken') || cookieUtils.getCookie('accessToken');
  },

  setToken: (token) => {
    localStorage.setItem('accessToken', token);
    cookieUtils.setCookie('accessToken', token);
  },

  getRefreshToken: () => {
    // Try localStorage first, then cookies
    return localStorage.getItem('refreshToken') || cookieUtils.getCookie('refreshToken');
  },

  setRefreshToken: (refreshToken) => {
    localStorage.setItem('refreshToken', refreshToken);
    cookieUtils.setCookie('refreshToken', refreshToken);
  },

  removeTokens: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    cookieUtils.removeCookie('accessToken');
    cookieUtils.removeCookie('refreshToken');
  },

  setTokens: (tokens) => {
    if (tokens.accessToken || tokens.token) {
      tokenStorage.setToken(tokens.accessToken || tokens.token);
    }
    if (tokens.refreshToken) {
      tokenStorage.setRefreshToken(tokens.refreshToken);
    }
  }
};

// Flag to prevent multiple refresh requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Create axios instances
export const authApi = axios.create({
  baseURL: AUTH_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export const voteApi = axios.create({
  baseURL: VOTE_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export const multipartVoteApi = axios.create({
  baseURL: VOTE_BASE_URL,
  headers: {
    "Content-Type": "multipart/form-data",
  },
});

export const uploadApi = axios.create({
  baseURL: AUTH_BASE_URL,
  headers: {
    "Content-Type": "multipart/form-data",
  },
});

export const questionnaireApi = axios.create({
  baseURL: QUESTIONNAIRE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export const shortsVideoApi = axios.create({
  baseURL: SHORTS_VIDEO_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export const shortsVideoApiMultipart = axios.create({
  baseURL: SHORTS_VIDEO_URL,
  headers: {
    "Content-Type": "multipart/form-data",
  },
});

export const affliateDetails = axios.create({
  baseURL: AUTH_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});


// Array of all API instances that need token management
const apiInstances = [
  authApi,
  voteApi,
  multipartVoteApi,
  uploadApi,
  questionnaireApi,
  shortsVideoApi,
  shortsVideoApiMultipart
];

// Request interceptor to add authorization header
const requestInterceptor = (config) => {
  const token = tokenStorage.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

// Response interceptor to handle token refresh
const responseInterceptor = (apiInstance) => (response) => response;

const responseErrorInterceptor = (apiInstance) => async (error) => {
  const originalRequest = error.config;

  if (error.response?.status === 401 && !originalRequest._retry) {
    if (isRefreshing) {
      // If already refreshing, queue this request
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiInstance(originalRequest);
      }).catch(err => {
        return Promise.reject(err);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshTokenValue = tokenStorage.getRefreshToken();

    if (!refreshTokenValue) {
      // No refresh token available, redirect to login
      tokenStorage.removeTokens();
      processQueue(error, null);
      isRefreshing = false;
      // You might want to redirect to login page here
      // window.location.href = '/login';
      return Promise.reject(error);
    }

    try {
      // Import refreshToken function dynamically to avoid circular imports
      const { refreshToken } = await import('./auth');
      const response = await refreshToken(refreshTokenValue);

      // Ensure response has the expected structure
      if (!response || (!response.accessToken && !response.token)) {
        throw new Error('Invalid refresh token response');
      }

      // Update stored tokens
      tokenStorage.setTokens(response);

      const newToken = response.accessToken || response.token;
      processQueue(null, newToken);

      // Retry original request with new token
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return apiInstance(originalRequest);
    } catch (refreshError) {
      // Refresh failed, remove tokens and redirect to login
      console.error('Token refresh failed:', refreshError);
      tokenStorage.removeTokens();
      processQueue(refreshError, null);
      // You might want to redirect to login page here
      // window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }

  return Promise.reject(error);
};

// Apply interceptors to all API instances
apiInstances.forEach(apiInstance => {
  apiInstance.interceptors.request.use(requestInterceptor);
  apiInstance.interceptors.response.use(
    responseInterceptor(apiInstance),
    responseErrorInterceptor(apiInstance)
  );
});

// Helper function to set tokens after successful login
export const setAuthTokens = (tokens) => {
  tokenStorage.setTokens(tokens);
};

// Helper function to clear tokens on logout
export const clearAuthTokens = () => {
  tokenStorage.removeTokens();
};

// Debug utility functions
export const debugTokenStorage = () => {
  const accessToken = tokenStorage.getToken();
  const refreshToken = tokenStorage.getRefreshToken();

  return {
    accessToken,
    refreshToken,
    hasTokens: !!(accessToken && refreshToken)
  };
};

// Debug function to check active environment
export const debugEnvironment = () => {
  return {
    currentEnv: ENV,
    availableEnvs: Object.keys(API_ROOTS),
    activeUrls: {
      AUTH_BASE_URL,
      VOTE_BASE_URL,
      SHORTS_VIDEO_URL,
      QUESTIONNAIRE_URL
    },
    envVar: process.env.REACT_APP_ENV
  };
};

// Check if user is authenticated
export const isAuthenticated = () => {
  const accessToken = tokenStorage.getToken();
  return !!accessToken;
};

// Utility functions for consistent token and userId access
export const getUserId = () => {
  return localStorage.getItem('userId');
};

export const getToken = () => {
  return tokenStorage.getToken();
};


// For backward compatibility with existing code
export const getCookieUserId = () => {
  // This function provides backward compatibility
  // It tries localStorage first, then falls back to cookies
  return localStorage.getItem('userId') || cookieUtils.getCookie('userId');
};

export const getCookieToken = () => {
  // This function provides backward compatibility
  // It uses our tokenStorage system
  return tokenStorage.getToken();
};

export * from './Vote';
export * from './events';
